import React from "react";
import { useNavigate } from "react-router-dom";
import {
  PortalPageShell,
  PortalStatusChip,
  PortalActionCard,
  PortalOverviewCard,
  TourLifecycleStepper,
  PortalPrimaryBtn,
  fmtDate,
  humanTourStatus,
  deriveLifecycleSteps,
} from "@/pages/portal/PortalShared";
import { usePortal } from "@/pages/portal/PortalContext";

const greetingFor = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export default function PortalOverview() {
  const nav = useNavigate();
  const { me, activeTour, quote, docs, routing } = usePortal();

  const submitted = routing?.submitted || null;
  const sharedCount = (routing?.shared || []).length;
  const steps = deriveLifecycleSteps({ tour: activeTour, quote, docs, routing });
  const status = humanTourStatus(activeTour?.status);

  const quoteAwaiting = quote && quote.status === "sent";
  const quoteAccepted = quote && quote.status === "accepted";
  const quoteDeclined = quote && quote.status === "declined";

  const routingSubmitted = !!submitted || sharedCount > 0;
  const routingUnderReview = submitted && submitted.status !== "reviewed";

  // Priority: Quote awaiting > Routing needed > Routing under review > All set
  let actionPanel;
  if (quoteAwaiting) {
    actionPanel = (
      <PortalActionCard
        tone="lime"
        eyebrow="Action Required"
        title="Quote Ready — Review and approve your transportation quote."
        description={`Quote v${quote.version} is awaiting your approval.`}
        action={
          <PortalPrimaryBtn data-testid="portal-action-review-quote" onClick={() => nav("/portal/quotes")}>
            Review Quote
          </PortalPrimaryBtn>
        }
        testid="portal-action-panel-quote"
      />
    );
  } else if (!routingSubmitted && activeTour) {
    actionPanel = (
      <PortalActionCard
        tone="lime"
        eyebrow="Action Required"
        title="Routing Needed — Submit your current tour routing for review."
        description="Paste, upload a PDF, or upload a spreadsheet. We'll take it from there."
        action={
          <PortalPrimaryBtn data-testid="portal-action-submit-routing" onClick={() => nav("/portal/routing")}>
            Submit Routing
          </PortalPrimaryBtn>
        }
        testid="portal-action-panel-routing"
      />
    );
  } else if (routingUnderReview) {
    actionPanel = (
      <PortalActionCard
        tone="hold"
        eyebrow="In Progress"
        title="Routing Under Review"
        description="All Access is reviewing your submission. We'll follow up shortly."
        testid="portal-action-panel-review"
      />
    );
  } else {
    actionPanel = (
      <PortalActionCard
        tone="lime"
        eyebrow="All Clear"
        title="You're all set."
        description="Nothing outstanding on your side. Your Account Manager will reach out if anything is needed."
        action={<span style={{ color: "#B7E31D", fontSize: 24, fontWeight: 700 }}>✓</span>}
        testid="portal-action-panel-clear"
      />
    );
  }

  return (
    <div className="max-w-[880px] mx-auto space-y-6" data-testid="page-portal-overview">
      {/* Welcome */}
      <div>
        <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold"
             style={{ color: "var(--fg-muted)" }} data-testid="portal-welcome-eyebrow">
          All Access Coach Leasing · Client Portal
        </div>
        <h1
          className="mt-2 text-[24px] md:text-[28px] font-semibold leading-tight"
          style={{ color: "var(--fg-primary)", fontFamily: "'Montserrat', 'Inter', sans-serif" }}
          data-testid="portal-welcome-greeting"
        >
          {greetingFor()}, {me?.display_name || "Client"}
        </h1>
        {activeTour && (
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <span className="text-[16px] md:text-[18px]" style={{ color: "var(--fg-secondary)" }}
                  data-testid="portal-welcome-tour">
              {activeTour.display_name}
            </span>
            <PortalStatusChip tone={status.tone} testid="portal-welcome-status">{status.label}</PortalStatusChip>
          </div>
        )}
      </div>

      {/* Action Required */}
      {actionPanel}

      {/* Tour Hero */}
      {activeTour && (
        <div
          className="relative rounded-md overflow-hidden"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-default)",
          }}
          data-testid="portal-tour-hero"
        >
          {/* Subtle SVG road-line watermark */}
          <svg
            aria-hidden
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
            viewBox="0 0 800 240"
          >
            <path d="M0 200 Q 200 40 400 160 T 800 100" stroke="var(--fg-muted)"
                  strokeWidth="1.5" strokeDasharray="6 12" fill="none" opacity="0.10" />
            <path d="M0 220 Q 220 80 440 200 T 800 140" stroke="var(--fg-muted)"
                  strokeWidth="1.5" strokeDasharray="6 12" fill="none" opacity="0.06" />
          </svg>

          <div className="relative p-6 md:p-8">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold"
                     style={{ color: "var(--fg-muted)" }}>
                  Current Tour
                </div>
                <div
                  className="mt-1 text-[26px] md:text-[32px] font-semibold leading-tight break-words"
                  style={{ color: "var(--fg-primary)", fontFamily: "'Montserrat', 'Inter', sans-serif" }}
                  data-testid="portal-hero-name"
                >
                  {activeTour.display_name}
                </div>
              </div>
              <PortalStatusChip tone={status.tone}>{status.label}</PortalStatusChip>
            </div>

            {/* Metadata grid */}
            <dl className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <dt className="uppercase text-[10px] tracking-[0.14em] font-semibold"
                    style={{ color: "var(--fg-muted)" }}>Start</dt>
                <dd className="mt-1 text-[13.5px]" style={{ color: "var(--fg-primary)" }}
                    data-testid="portal-hero-start">
                  {fmtDate(activeTour.start_date)}
                </dd>
              </div>
              <div>
                <dt className="uppercase text-[10px] tracking-[0.14em] font-semibold"
                    style={{ color: "var(--fg-muted)" }}>End</dt>
                <dd className="mt-1 text-[13.5px]" style={{ color: "var(--fg-primary)" }}
                    data-testid="portal-hero-end">
                  {fmtDate(activeTour.end_date)}
                </dd>
              </div>
              <div>
                <dt className="uppercase text-[10px] tracking-[0.14em] font-semibold"
                    style={{ color: "var(--fg-muted)" }}>Client</dt>
                <dd className="mt-1 text-[13.5px]" style={{ color: "var(--fg-primary)" }} data-testid="portal-hero-client-name">
                  {me?.display_name || activeTour.customer_name || "—"}
                </dd>
              </div>
              {/* RC4 · Portal ITEM 7 — tour number/ID intentionally NOT
                  rendered in the client-facing portal. Removed the
                  duplicate second "Client" tile that used to follow. */}
            </dl>
          </div>
        </div>
      )}

      {/* Lifecycle */}
      {activeTour && (
        <div
          className="rounded-md p-5 md:p-6"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
          data-testid="portal-lifecycle-wrap"
        >
          <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold mb-4"
               style={{ color: "var(--fg-muted)" }}>
            Tour Lifecycle
          </div>
          <TourLifecycleStepper steps={steps} />
        </div>
      )}

      {/* Overview cards */}
      {activeTour && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <PortalOverviewCard
            label="Current Tour"
            primary={activeTour.display_name}
            meta={`${fmtDate(activeTour.start_date)} → ${fmtDate(activeTour.end_date)}`}
            onClick={() => nav("/portal/tour")}
            testid="portal-card-tour"
          />
          <PortalOverviewCard
            label="Routing"
            primary={routingSubmitted ? "Submitted for review" : "Not yet submitted"}
            meta={routingSubmitted ? "AI-Assisted Draft" : "Paste, PDF, or spreadsheet"}
            onClick={() => nav("/portal/routing")}
            testid="portal-card-routing"
          />
          <PortalOverviewCard
            label="Quote"
            primary={
              quoteAccepted ? "Approved" :
              quoteDeclined ? "Declined" :
              quoteAwaiting ? `Quote v${quote.version} — Awaiting` :
              "Not yet issued"
            }
            meta={quote ? "Tap to view proposal" : "Once routing is reviewed"}
            onClick={() => nav("/portal/quotes")}
            testid="portal-card-quote"
          />
          <PortalOverviewCard
            label="Documents"
            primary={docs.length > 0 ? `${docs.length} available` : "None yet"}
            meta="Quotes, contracts, tour docs"
            onClick={() => nav("/portal/documents")}
            testid="portal-card-documents"
          />
        </div>
      )}

      {!activeTour && (
        <div className="rounded-md p-8 text-center"
             style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", color: "var(--fg-secondary)" }}
             data-testid="portal-no-tour">
          No tours on file yet. Your Account Manager will reach out once your first tour is scheduled.
        </div>
      )}
    </div>
  );
}
